import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Toolbar, Item } from 'devextreme-react/toolbar';
import { DataGrid } from 'devextreme-react';
import { Editing, Texts, Column, Scrolling } from 'devextreme-react/data-grid';
import { loader } from 'graphql.macro';
import { useDate, useDocCloseDate } from 'hooks';
import { CustomRule } from 'devextreme-react/validator';
import { custom } from 'devextreme/ui/dialog';
import { ClickEvent } from 'devextreme/ui/button';
import { CellPreparedEvent, ContextMenuPreparingEvent } from 'devextreme/ui/data_grid';
import { showWarning } from 'utils/notify';
import dayjs from 'dayjs';
import { WorktimeDoc, WorktimeRow, LegendItem } from './worktimeTypes';
import { fetchGraphQLData } from 'utils/gql-datasource';
const getWorkersFrom1C = loader('./gql/getWorkersFrom1C.gql');

interface WorkersGridProps {
  data: WorktimeDoc,
  value: WorktimeRow[],
  onChange: (rows: WorktimeRow[]) => void,
  legend: LegendItem[],
  docReadOnly: boolean,
}

interface Worker1C {
  person_id: string;
  hours: {
    day: number;
    hoursgraph: number;
  }[];
}

interface ValidatorHoursProps {
  data: { person_id: string };
  column: { dataField: string };
  rule: { message: string };
  value: string | number;
}

type WorkerGraphMap = Map<string, Map<string, number>>;

export const WorkersGrid = (props:WorkersGridProps) => {

  const data = props.value;
  const setData = props.onChange;
  const [ dataGraph, setDataGraph ] = useState<WorkerGraphMap>(new Map());
  const docReadOnly = props.docReadOnly;
  const { formatDate, startOfMonth } = useDate();
  const protectDate = useDocCloseDate('doc.work_time');

  const servicesDataGridRef = useRef<DataGrid>(null);

  const legend = props.legend;

  const fillValidatorGraph = useCallback(async() => {
    const _data = await fetchGraphQLData(getWorkersFrom1C, { period_reg: formatDate(props.data.period_reg) });
    const workers = (_data?.worktime?.workers || []) as Array<Worker1C>;
    if (workers.length) {
      const workerGraphMap = new Map();
      workers.forEach((wrow) => {
        const hoursGraphMap = new Map();
        wrow.hours.forEach((hrow) => hoursGraphMap.set(`d_${hrow.day}`, hrow.hoursgraph));
        workerGraphMap.set(wrow.person_id, hoursGraphMap);
      });
      setDataGraph(workerGraphMap);
    }
  }, [ formatDate, props.data.period_reg ]);

  useEffect(() => {
    const daysInPeriod = dayjs(props.data.period_reg).endOf('month').date();

    const worktable: WorktimeRow[] = (props.data?.worktable ?? []).map((row) => {
      const obj = { ...row };
      if (row?.hours?.length > 0) {
        obj.hours = row.hours.filter((r) => r.day <= daysInPeriod); // відкидаємо дні, яких нема у поточному періоді
        obj.hours.forEach((hrow) => { obj[`d_${hrow.day}`] = hrow.hours; }); // розкладаємо в d_N поля для гріда
      }
      return obj;
    });

    if (worktable.length) fillValidatorGraph();
    setData(worktable);
  }, [ props.data.worktable, props.data.period_reg, fillValidatorGraph ]);

  const updateWorkerData = (existingData: WorktimeRow[], newWorkers: WorktimeRow[]) => {
    // Filter out new workers not already in the existing data
    const filteredNewWorkers = newWorkers.filter(
      (newWorker) => !existingData.find((existing) => existing.person_id === newWorker.person_id)
    );

    // Update existing workers with new data
    const updatedData = existingData.map((existing) => {
      const updatedWorker = newWorkers.find((newWorker) => newWorker.person_id === existing.person_id);
      if (updatedWorker) {
        return {
          ...existing,
          graphic_id: updatedWorker.graphic_id,
          person_fio: updatedWorker.person_fio,
          graphic_name: updatedWorker.graphic_name,
          positions: updatedWorker.positions,
        };
      }
      return existing;
    });

    // Merge updated data with new workers
    return [ ...updatedData, ...filteredNewWorkers ];
  };

  const fillGrid = async(e: ClickEvent) => {

    const _data = await fetchGraphQLData(getWorkersFrom1C, { period_reg: formatDate(props.data.period_reg) });
    if (!_data) return;

    const workers: WorktimeRow[] = _data?.worktime?.workers || [];

    if (workers.length) {
      // Add day-specific hours to each worker
      workers.forEach((worker) => {
        worker.hours.forEach((hour) => { worker[`d_${hour.day}`] = hour.hours; });
      });

      if (e?.element.id === 'refresh') {
        const updatedData = updateWorkerData(data, workers);
        setData(updatedData);
        return;
      }
    } else {
      showWarning('Відсутні прийняті в лабораторію працівники');
    }

    setData(workers);
    fillValidatorGraph();
  };

  const confirmOverwrite = (): Promise<boolean> => custom({
    title: 'Таблиця заповнена.',
    messageHtml: `
      <p style="text-align:center;">
      <b>Заповнення перезапише усі дані<br/>
      Продовжити?
      </b></p>`,
    buttons: [
      { text: 'Так', onClick: () => true },
      { text: 'Ні', onClick: () => false },
    ],
  }).show();

  const fillButtonOptions = {
    text: 'Заповнити',
    icon: 'download',
    onClick: async(e: ClickEvent) => {
      if (data.length && !(await confirmOverwrite())) return;
      fillGrid(e);
    },
    hint: 'Заповнити за даними обліку',
  };
  const refreshButtonOptions = {
    text: 'Обновити',
    elementAttr: { id: 'refresh' },
    icon: 'import',
    onClick: (e: ClickEvent) => fillGrid(e),
    hint: 'Оновити перелік працівників',
  };

  const isSunday = useCallback((day: number) => dayjs(props.data.period_reg).startOf('month').add(day - 1, 'day').day() === 0, [ props.data.period_reg ]);

  const renderDayHeader = (day: number) => <div style={{ color: isSunday(day) ? 'red' : 'inherit' }}>{day}</div>;

  const validatorHours = useCallback((e:ValidatorHoursProps) => {
    const {
      data: { person_id },
      column: { dataField },
      rule,
      value,
    } = e;
    const personLimit = dataGraph.get(person_id)?.get(dataField) || 0;
    const limit = personLimit;
    rule.message = `Максимум-мінімум: ${limit}`;
    return ((limit === Number(value)) || legend.find((r) => r.value === value));
  }, [ dataGraph, legend ]);


  const hoursColumns = useMemo(() => {
    if (!data[0]?.hours) return null;

    const periodStart = dayjs(props.data.period_reg).startOf('month');
    const protectStart = dayjs(protectDate).startOf('day');
    const today = dayjs().get('date');
    const isFuturePeriod = startOfMonth() > startOfMonth(props.data.period_reg);

    const isProtectedDay = (day: number) => periodStart.add(day - 1, 'day').isBefore(protectStart);
    const isFutureDay = (day: number) => day > today && !isFuturePeriod;

    const isDayEditable = (day: number) => !docReadOnly && !isSunday(day) && !isProtectedDay(day) && !isFutureDay(day);

    return data[0].hours.map((rec) => (
      <Column
        key={rec.day}
        alignment="center"
        cssClass="otk-cell-narrow-padding"
        dataField={`d_${rec.day}`}
        caption={rec.day}
        allowEditing={isDayEditable(rec.day)}
        allowSorting={false}
        headerCellRender={() => renderDayHeader(rec.day)}
        width={36}
        dataType="string"
      >
        <CustomRule reevaluate={true} ignoreEmptyValue={true} validationCallback={validatorHours}/>
      </Column>
    ));
  }, [ data, protectDate, props.data.period_reg, startOfMonth, isSunday, validatorHours, docReadOnly ]);

  const highlightLockedCell = (e: CellPreparedEvent<WorktimeRow>) => {
    if (e.column?.name?.startsWith('d_') && !e.column.allowEditing) {
      e.cellElement.style.background = 'lightgrey';
    }
  };

  const prepareDayContextMenu = (e: ContextMenuPreparingEvent<WorktimeRow>) => {
    if (!e.column?.name?.startsWith('d_')) return;
    const dayField = e.column.name as `d_${number}`;

    if (e.row?.rowType === 'header' || !e.column.allowEditing) {
      e.items = [];
      return;
    }

    e.items = legend.map((r) => ({
      text: `${r.value} - ${r.caption}`,
      onItemClick: () => {
        if (e.row?.data) e.row.data[dayField] = r.value;
        servicesDataGridRef.current?.instance.refresh(true);
      },
    }));
  };

  // useTraceUpdate({startOfMonth, formatDate})
  return (
    <>
      <Toolbar className='otk-dg-toolbar-border dx-theme-border-color'>
        <Item
          location='before'
          locateInMenu='auto'
          widget='dxButton'
          disabled={docReadOnly || data.length}
          options={fillButtonOptions}
        />
        <Item
          location='before'
          locateInMenu='auto'
          widget='dxButton'
          disabled={docReadOnly || !data.length}
          options={refreshButtonOptions}
        />
      </Toolbar>
      <DataGrid
        ref={servicesDataGridRef}
        noDataText='Список порожній'
        remoteOperations={false}
        rowAlternationEnabled
        showBorders
        showColumnLines
        allowColumnResizing
        hoverStateEnabled
        columnResizingMode='widget'
        columnAutoWidth
        keyExpr={'person_id'}
        dataSource={data}
        focusedRowEnabled={!docReadOnly}
        errorRowEnabled={false}
        onCellPrepared={highlightLockedCell}
        onContextMenuPreparing={prepareDayContextMenu}
        // onRowUpdating={handleRowUpdating}
        >
        <Editing
          mode='cell'
          allowAdding={false}
          allowUpdating={!docReadOnly}
          allowDeleting={false}
          useIcons={true}
          confirmDelete={true}>
          <Texts confirmDeleteMessage='Вилучити?' deleteRow='вилучити' />
        </Editing>
        <Scrolling showScrollbar='always' useNative={true}/>

        <Column dataField='person_fio' caption='Працівник' allowEditing={false} fixed fixedPosition='left'/>
        <Column dataField='graphic_name' caption='Графік' allowEditing={false} fixed fixedPosition='left'/>
        <Column dataField='positions' caption='Ставок' width={60} allowEditing={false} fixed fixedPosition='left'/>
        {data.length > 0 && hoursColumns}

      </DataGrid>
    </>
  );
};


